import { useCallback, useEffect, useMemo, useRef } from 'react';
import { GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import tw, { styled } from 'twin.macro';
import { useAtom, useSetAtom, useAtomValue } from 'jotai';
import debounce from 'lodash.debounce';
import { useTranslation } from 'next-i18next';
import { useIntercom } from 'react-use-intercom';

import { SkeletonEmployerCardListLoading, SkeletonTextLoading } from '@restworld/ui-ds';
import { useAnalytics, useIsFreemiumUser } from '@restworld/utils-common';
import { useAuth } from '@restworld/feature-authentication';

import EmployerPrimaryLayout from '../../../layouts/employer-primary-layout';
import { getServerTranslations } from '../../../get-server-side-translations';
import useEmployerJobListings from '../../../components/job-listing/hooks/use-employer-job-listing';
import { useCheckInvoiceStatus } from '../../../components/job-listing/hooks/use-check-invoice-status';
import {
  employerJobListingSearchQueryAtom,
  employerJobListingSortValueAtom,
  employerJobListingJpStatusFilterAtom,
  employerJobListingRestaurantNameFilterAtom,
  employerJobListingLocationFilterAtom,
  invoiceStatusAtom,
} from '../../../components/job-listing/employer-job-listing.atoms';
import EmployerJobListingFilters, {
  computeJobListingFiltersCount,
  IEmployerFilterForm,
} from '../../../components/job-listing/employer-job-listing-filters';
// import useEmployerJobListingSortMenuList from '../../../components/job-listing/hooks/use-employer-job-listing-sort-menu-list';
import EmployerJobListingCardsList from '../../../components/job-listing/employer-job-listing-cards-list';
import FreemiumUserJPPageModal, {
  isFreemiumUserJpPageModalOpenAtom,
} from '../../../components/job-listing/freemium-user-jp-page-modal';
import UnpaidInvoicesModal from '../../../components/job-position-onboarding/modals/unpaid-invoices-modal';
import { isUnpaidInvoicesModalOpenAtom } from '../../../components/job-position-onboarding/atoms/unpaid-invoices-modal.atoms';
import { InvoiceBlockPage } from '../../../components/invoice-block-page';

export const baseUrl = process.env.NEXT_PUBLIC_BASE_API_URL;

function JobListingPage() {
  const { t: translateCommon } = useTranslation('common');
  const router = useRouter();
  const { trackEvent } = useAnalytics();
  const { startTour } = useIntercom();
  const { user } = useAuth();
  const [employerJobListingSearchQuery, setEmployerJobListingSearchQuery] = useAtom(employerJobListingSearchQueryAtom);
  const [employerJobListingSortValue /*setEmployerJobListingSortValue */] = useAtom(employerJobListingSortValueAtom);
  const [jpStatus, setJpStatus] = useAtom(employerJobListingJpStatusFilterAtom);
  const [restaurantName, setRestaurantName] = useAtom(employerJobListingRestaurantNameFilterAtom);
  const [location, setLocation] = useAtom(employerJobListingLocationFilterAtom);
  // const [, setShowContactRestworldManagerModal] = useAtom(showContactRestWorldManagerModalAtom);
  const filterFormRef = useRef<IEmployerFilterForm | null>(null);
  const isFreemiumUser = useIsFreemiumUser();
  const setIsFreemiumUserJpPageModalOpen = useSetAtom(isFreemiumUserJpPageModalOpenAtom);
  const setIsUnpaidInvoicesModalOpen = useSetAtom(isUnpaidInvoicesModalOpenAtom);
  const invoiceStatus = useAtomValue(invoiceStatusAtom);
  const setInvoiceStatus = useSetAtom(invoiceStatusAtom);

  // Check invoice status only once on page load
  const { data: invoiceStatusData, isChecking: isCheckingInvoiceStatus } = useCheckInvoiceStatus({
    organizationId: user?.organization_id,
    enabled: !!user?.organization_id,
  });

  // Store invoice status in atom when data is available
  useEffect(() => {
    if (invoiceStatusData) {
      setInvoiceStatus(invoiceStatusData);
    }
  }, [invoiceStatusData, setInvoiceStatus]);

  // const sortMenuList = useEmployerJobListingSortMenuList();
  const { jobPositionItems, hits, query, hasNextPage, loading } = useEmployerJobListings({
    searchVector: employerJobListingSearchQuery,
    orderBy: [employerJobListingSortValue],
    status: jpStatus?.length ? jpStatus[0] : undefined,
    restaurantName,
    location,
  });
  const totalSearchResults = useMemo(
    () =>
      loading ? (
        <SkeletonTextLoading width="medium" tw="mt-2" />
      ) : (
        `${hits || 0} ${hits === 1 ? translateCommon('job_position') : translateCommon('job_positions')}`
      ),
    [hits, loading, translateCommon]
  );

  const jobListingFiltersCount = useMemo(
    () => computeJobListingFiltersCount({ restaurantName, jpStatus, location }),
    [restaurantName, jpStatus, location]
  );

  const searchProps = useMemo(
    () => ({
      placeholder: 'what_job_position_are_you_looking_for',
      onChange: debounce((value) => setEmployerJobListingSearchQuery(value), 500),
    }),
    [setEmployerJobListingSearchQuery]
  );
  const noResultsFoundProps = useMemo(
    () => ({
      title: translateCommon('no_jobs_found_title'),
      content: translateCommon('no_jobs_found_content'),
      foundNoResults: !query.isLoading && jobPositionItems?.length === 0,
    }),
    [jobPositionItems?.length, query.isLoading, translateCommon]
  );
  const filterProps = useMemo(
    () => ({
      element: <EmployerJobListingFilters ref={filterFormRef} />,
      filterCount: jobListingFiltersCount,
      formRef: filterFormRef,
    }),
    [jobListingFiltersCount]
  );
  const loadMoreBtnProps = useMemo(
    () => ({
      onClick: () => query.fetchNextPage(),
      loading: query.isFetchingNextPage,
      hasNextPage,
    }),
    [hasNextPage, query]
  );

  const onAddButtonClick = useCallback(async () => {
    trackEvent('employer_open_new_position', { email: user?.email, phone: user?.phone_number });

    // Only check invoice status for users on PPU business model
    if (user?.using_ppu_business_model === true) {
      // Use stored invoice status to determine navigation
      if (invoiceStatus?.all_paid) {
        router.push('/employer/job-positions/onboarding');
      } else {
        setIsUnpaidInvoicesModalOpen(true);
      }
    } else {
      // Users not on PPU model can always create jobs
      router.push('/employer/job-positions/onboarding');
    }
  }, [
    trackEvent,
    user?.email,
    user?.phone_number,
    user?.using_ppu_business_model,
    invoiceStatus,
    router,
    setIsUnpaidInvoicesModalOpen,
  ]);
  const onInfoIconClick = useCallback(() => startTour(599323), [startTour]);

  const primaryLayoutProps = useMemo(
    () => ({
      totalSearchResults,
      searchProps,
      addButtonTitle: 'open_new_position',
      pageTitle: 'job_positions_key',
      noResultsFoundProps,
      filterProps,
      title: 'job_positions_title',
      onAddButtonClick,
      loadMoreBtnProps,
      loading: loading || isCheckingInvoiceStatus,
      isResultAndFilterElAlwaysOnSameLine: true,
      onInfoIconClick,
      addButtonLoading: false,
    }),
    [
      filterProps,
      loadMoreBtnProps,
      loading,
      noResultsFoundProps,
      onAddButtonClick,
      onInfoIconClick,
      searchProps,
      totalSearchResults,
      isCheckingInvoiceStatus,
    ]
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const jpStatus = urlParams.get('jpStatus');
    const location = urlParams.get('location');
    const restaurantName = urlParams.get('restaurantName');
    if (jpStatus) setJpStatus([jpStatus]);
    if (location) setLocation(location);
    if (restaurantName) setRestaurantName(restaurantName);
  }, [setJpStatus, setLocation, setRestaurantName]);

  useEffect(() => {
    if (isFreemiumUser) setIsFreemiumUserJpPageModalOpen(true);
  }, [isFreemiumUser, setIsFreemiumUserJpPageModalOpen]);

  // Show InvoiceBlockPage if block_completely is true (only for PPU business model users)
  if (user?.using_ppu_business_model === true && invoiceStatus?.block_completely) {
    return <InvoiceBlockPage />;
  }

  return (
    <>
      <EmployerPrimaryLayout {...primaryLayoutProps}>
        {loading || isCheckingInvoiceStatus ? (
          <SkeletonEmployerCardListLoading />
        ) : (
          <CardsContainer>
            <EmployerJobListingCardsList jobPositionItems={jobPositionItems} />
          </CardsContainer>
        )}
      </EmployerPrimaryLayout>
      <FreemiumUserJPPageModal />
      <UnpaidInvoicesModal />
    </>
  );
}

export default JobListingPage;

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const translation = await getServerTranslations(locale, ['common', 'job-positions-page', 'jp-onboarding']);
  return {
    props: {
      ...translation,
    },
  };
};

const CardsContainer = styled.div`
  ${tw`grid grid-cols-1 gap-4`}
`;
